<script setup lang="ts">
import {
  AtomText,
  AtomButton,
  useToast,
  AtomInput,
  SatFormControl,
} from "@chatfood/design-system";
import { bemBuilder } from "@chatfood/core-utils";
import { ref } from "vue";
import type { IDiscountsListEmits } from "./emits";
import { useLocale } from "~/composables/use-locale";
import { useDiscountsStore } from "~/stores/discounts";
import { useBasketStore } from "~/stores/basket";
import { DiscountDetails } from "~/components/discount-details";

const css = bemBuilder("discounts-list");

const emits = defineEmits<IDiscountsListEmits>();

const { t } = useLocale();
const { toastSuccess } = useToast();

const basket = useBasketStore();
const discounts = useDiscountsStore();

const discountCode = ref("");
const isCheckingDiscountCode = ref(false);
const discountErrorMessage = ref<string>();

const onApplyCode = async (code: string) => {
  isCheckingDiscountCode.value = true;
  discountErrorMessage.value = undefined;

  try {
    if (basket.discount) {
      await basket.removeDiscount();
    }

    await basket.applyDiscount(code);
    toastSuccess(t("components.discounts_list.success_toast"));

    onDismiss();
  } catch {
    discountErrorMessage.value = t(
      "components.discount_view.invalid_code_message",
    );
  } finally {
    isCheckingDiscountCode.value = false;
  }
};

function onDismiss() {
  emits("dismiss");
}
</script>

<template>
  <div :class="css('')">
    <div :class="css('header')">
      <AtomText type="h2">
        {{ t("components.discounts_list.title") }}
      </AtomText>
    </div>
    <SatFormControl
      :class="css('input-group')"
      :error-message="discountErrorMessage"
    >
      <div :class="css('apply-discount')">
        <AtomInput
          :class="css('input')"
          :placeholder="t('components.discounts_list.input_placeholder')"
          :value="discountCode"
          :has-error="Boolean(discountErrorMessage)"
          autofocus
          @change="(value) => (discountCode = value)"
        />
        <AtomButton
          variant="secondary"
          theme="tailored"
          :is-loading="isCheckingDiscountCode"
          @click="onApplyCode(discountCode)"
        >
          {{ t("components.discounts_list.cta_apply_label") }}
        </AtomButton>
      </div>
    </SatFormControl>
    <div v-if="discounts.hasDiscounts" :class="css('separator')" />
    <div :class="css('content')">
      <AtomText
        v-if="discounts.hasDiscounts"
        tag="div"
        color="color-text-headline"
        type="small-bold"
      >
        {{ t("components.discounts_list.available_discounts") }}
      </AtomText>
      <div
        v-for="discount in discounts.data"
        :key="discount.id"
        :class="css('item')"
      >
        <DiscountDetails :discount="discount" @close="onDismiss" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.discounts-list {
  width: 576px;
  max-width: 100%;
  display: flex;
  gap: var(--spacing-lg);
  flex-direction: column;
  padding: var(--spacing-lg);

  @media (width > $breakpoint-sm) {
    max-height: 90vh;
    padding-bottom: var(--spacing-lg);
  }

  &__apply-discount {
    display: flex;
    gap: var(--spacing-sm);
  }

  &__separator {
    height: 1px;
    width: 100%;
    background-color: var(--color-base-95);
    display: flex;
  }

  &__input-group {
    display: flex;
    width: 100%;
    gap: var(--spacing-sm);
  }

  &__input {
    flex: 1;
  }

  &__content {
    margin: calc(var(--spacing-lg) * -1);
    padding: var(--spacing-lg);

    @media (width > $breakpoint-sm) {
      overflow: auto;
      max-height: 90vh;
      padding-block: 0;
      margin-block: 0;
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-bottom: solid 1px var(--color-base-95);
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-lg);
    padding-bottom: var(--spacing-lg);
    margin-top: var(--spacing-md);

    &:last-child {
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }
}
</style>
